import { ChevronRightIcon, PlusIcon } from '@heroicons/react/24/outline';
import type { ProductDiscountRule, ProductRow } from '../../../services/billing';

function ruleBadgeLabel(rule: ProductDiscountRule): string {
    return rule.discountType === 'percentage'
        ? `-${rule.discountValue}%`
        : `-${rule.discountValue.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}`;
}

function ruleScheduleLabel(rule: ProductDiscountRule): string | null {
    const now = Date.now();
    if (rule.startsAt && new Date(rule.startsAt).getTime() > now) return 'scheduled';
    if (rule.endsAt && new Date(rule.endsAt).getTime() <= now) return 'ended';
    return null;
}

interface DiscountRulesSectionProps {
    product: ProductRow;
    onOpenRule: (rule: ProductDiscountRule | null) => void;
}

// Discount rules - live on the product; eligible practices get the rate.
// Saved through their own modal, not the product form.
export function DiscountRulesSection({ product, onOpenRule }: DiscountRulesSectionProps) {
    return (
        <div className="flex flex-col gap-2.5">
            <div className="flex items-baseline justify-between">
                <span className="text-[13px] font-semibold text-gray-700">Discount rules</span>
                <span className="text-[11px] text-gray-400">
                    Rules apply to this product; eligible practices get the rate. Rules stack.
                </span>
            </div>
            <div className="border border-gray-200 rounded-xl divide-y divide-gray-100 overflow-hidden">
                {product.discountRules.map((rule) => {
                    const schedule = ruleScheduleLabel(rule);
                    const appliesLabel = rule.appliesToVersionId
                        ? `${product.versions.find((v) => v.id === rule.appliesToVersionId)?.label ?? 'One'} version`
                        : 'All versions';
                    return (
                        <button
                            key={rule.id}
                            type="button"
                            onClick={() => onOpenRule(rule)}
                            className="w-full flex items-center gap-3 px-3.5 py-3 hover:bg-gray-50 text-left"
                        >
                            <span className="inline-flex text-[11px] font-bold px-2 py-0.5 rounded-md bg-green-50 text-green-600 tabular-nums shrink-0">
                                {ruleBadgeLabel(rule)}
                            </span>
                            <span className="min-w-0 flex-1">
                                <span className="block text-sm font-medium text-gray-800 truncate">
                                    {rule.name}
                                </span>
                                <span className="block text-xs text-gray-400">{appliesLabel}</span>
                            </span>
                            {schedule && (
                                <span
                                    className={`text-[11px] font-medium px-2 py-0.5 rounded-full shrink-0 ${
                                        schedule === 'scheduled'
                                            ? 'bg-blue-50 text-blue-600'
                                            : 'bg-gray-100 text-gray-500'
                                    }`}
                                >
                                    {schedule}
                                </span>
                            )}
                            <span className="text-[12px] text-gray-500 shrink-0">
                                {rule.eligibleCount}{' '}
                                {rule.eligibleCount === 1 ? 'practice' : 'practices'} eligible
                            </span>
                            <ChevronRightIcon className="h-4 w-4 text-gray-300 shrink-0" />
                        </button>
                    );
                })}
                <button
                    type="button"
                    onClick={() => onOpenRule(null)}
                    className="h-10 w-full text-[13px] font-semibold text-gray-500 hover:bg-gray-50 flex items-center justify-center gap-2"
                >
                    <PlusIcon className="h-4 w-4" strokeWidth={2} />
                    New discount rule
                </button>
            </div>
        </div>
    );
}
